import { supabase } from '../config/supabase';
import { User } from '../types/user';

export const getUsers = async (): Promise<User[]> => {
  // Añadimos un parámetro de timestamp para evitar el caché
  const timestamp = new Date().getTime();
  
  // Usamos una consulta RPC personalizada para evitar el caché
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .order('name')
    .limit(1000)  // Aseguramos que obtenemos todos los usuarios
    .throwOnError();  // Lanza un error si hay problemas con la consulta
  
  if (error) throw error;
  
  // Verificamos que data no sea null antes de mapearlo
  if (!data) return [];
  
  return data.map(user => ({
    id: user.id,
    email: user.email,
    name: user.name,
    role: user.role,
    propertyId: user.property_id,
    created_at: user.created_at
  }));
};

export const createUser = async (
  email: string,
  password: string,
  name: string,
  role: 'admin' | 'owner',
  propertyId?: string
): Promise<User> => {
  try {
    // 1. Crear usuario en Auth usando signUp
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { name, role }
        // Se elimina emailRedirectTo para evitar el envío de correos de verificación
      }
    });

    if (authError) {
      console.error('Error en la autenticación:', authError);
      throw new Error(`Error de autenticación: ${authError.message}`);
    }
    if (!authData.user) throw new Error('No se pudo crear el usuario en Auth');

    // 2. Crear usuario en la base de datos
    const { data, error: dbError } = await supabase
      .from('users')
      .insert([{
        id: authData.user.id,
        email,
        name,
        role,
        property_id: propertyId
      }])
      .select()
      .single();

    if (dbError) {
      console.error('Error en la base de datos:', dbError);
      throw new Error(`Error en la base de datos: ${dbError.message}`);
    }

    // 3. Si es propietario y tiene propiedad asignada, actualizar la propiedad
    if (role === 'owner' && propertyId) {
      const { error: propertyError } = await supabase
        .from('properties')
        .update({ owner_id: authData.user.id })
        .eq('id', propertyId);

      if (propertyError) {
        console.error('Error al actualizar la propiedad:', propertyError);
        throw new Error(`Error al actualizar la propiedad: ${propertyError.message}`);
      }
    }

    return {
      id: data.id,
      email: data.email,
      name: data.name,
      role: data.role,
      propertyId: data.property_id,
      created_at: data.created_at
    };
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const updateUser = async (user: Partial<User> & { id: string }): Promise<void> => {
  // Convertir string vacío a null para property_id
  const propertyId = user.propertyId && user.propertyId.trim() !== '' ? user.propertyId : null;
  
  const { error } = await supabase
    .from('users')
    .update({
      name: user.name,
      role: user.role,
      property_id: propertyId
    })
    .eq('id', user.id);

  if (error) throw error;

  // Si se está actualizando la propiedad asignada
  if (propertyId) {
    const { error: propertyError } = await supabase
      .from('properties')
      .update({ owner_id: user.id })
      .eq('id', propertyId);

    if (propertyError) throw propertyError;
  }
};

export const deleteUser = async (userId: string): Promise<void> => {
  try {
    console.log('Iniciando eliminación de usuario con ID:', userId);

    // Primero intentamos usar la Edge Function que tiene permisos de servicio
    console.log('Usando Edge Function delete-user...');
    try {
      // Obtenemos la URL de Supabase del entorno o configuración
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
      const edgeFunctionUrl = `${supabaseUrl}/functions/v1/delete-user`;
      
      // Obtenemos el token de autenticación de manera síncrona
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      
      if (!token) {
        console.error('No se pudo obtener el token de autenticación');
        throw new Error('No hay sesión de usuario activa');
      }
      
      console.log('Token obtenido, llamando a Edge Function...');
      
      const response = await fetch(edgeFunctionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId })
      });

      const result = await response.json();
      
      if (!response.ok) {
        console.error('Error en la respuesta de la Edge Function:', result);
        throw new Error(`Error en la Edge Function: ${result.error || 'Desconocido'}`);
      }

      console.log('Resultado de la Edge Function:', result);
      
      // Si la Edge Function fue exitosa, no necesitamos hacer nada más
      if (result.success) {
        console.log('Usuario eliminado completamente mediante Edge Function');
        return;
      }
      
      // Si hay una advertencia pero no un error, continuamos con el proceso
      if (result.warning) {
        console.warn('Advertencia de la Edge Function:', result.warning);
      }
    } catch (edgeFunctionError) {
      console.error('Error al llamar a la Edge Function:', edgeFunctionError);
      console.log('Intentando métodos alternativos...');
    }

    // Si la Edge Function falló o no está disponible, intentamos con la función RPC
    console.log('Usando función RPC delete_user_direct...');
    const { data: result, error: rpcError } = await supabase
      .rpc('delete_user_direct', { user_id: userId });

    // Verificamos el resultado de la función RPC
    if (rpcError) {
      console.error('Error al eliminar usuario con RPC:', rpcError);
      
      // Si falla la RPC, intentamos el método tradicional como respaldo
      console.log('La función RPC falló, usando método tradicional...');
      
      // 1. Eliminar la relación con la propiedad
      console.log('Paso 1: Eliminando relación con propiedades...');
      const { error: propertyError } = await supabase
        .from('properties')
        .update({ owner_id: null })
        .eq('owner_id', userId);

      if (propertyError) {
        console.error('Error al actualizar propiedades:', propertyError);
        throw propertyError;
      }
      
      // 2. Eliminar el usuario directamente con DELETE
      console.log('Paso 2: Eliminando usuario con DELETE directo...');
      const { error: deleteError } = await supabase
        .from('users')
        .delete()
        .eq('id', userId);
        
      if (deleteError) {
        console.error('Error al eliminar usuario con DELETE directo:', deleteError);
        throw deleteError;
      }
      
      console.log('Usuario eliminado mediante método tradicional');
    } else {
      // La función RPC se ejecutó correctamente
      console.log('Resultado de la función RPC:', result);
    }

    // Forzamos una pausa para asegurar que la base de datos se actualice
    console.log('Esperando a que la base de datos se actualice...');
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Verificamos que el usuario realmente se haya eliminado
    const { data: checkData, error: checkError } = await supabase
      .from('users')
      .select('id')
      .eq('id', userId);
      
    if (checkError) {
      console.error('Error al verificar eliminación:', checkError);
    } else {
      if (checkData && checkData.length > 0) {
        console.error('ADVERTENCIA: El usuario sigue existiendo en la base de datos después de intentar eliminarlo');
      } else {
        console.log('Verificación exitosa: El usuario ya no existe en la base de datos');
      }
    }

    console.log('Proceso de eliminación completado');
    return;

  } catch (error) {
    console.error('Error al eliminar usuario:', error);
    throw error;
  }
};
